import { useEffect, useState } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonForm from "../../../components/form/form";
import useCommonFetch from "../../../../hooks/useCommonFetch";
import {
  getWorkspaceDetail,
  updateWorkspace,
} from "../../../../services/workspace/workspace.service";
import toast from "react-hot-toast";

const GeneralSetup = () => {
  const [setSearch] = useState("");
  const [isEditValue, setIsEditValue] = useState(null);
  const [workspaceDetail, setWorkspaceDetail] = useState();

  const { handleUpdate } = useCommonFetch();
  const fetchWorkspaceDetail = () => {
    getWorkspaceDetail({ additional_info: true })
      .then((response) => {
        const ws = response.workspace;
        // Convert time from HH:mm:ss to HH:mm for time input
        const formatTimeForInput = (timeString) => {
          if (!timeString || timeString === "") return "";
          // If in HH:mm:ss format, remove seconds
          if (timeString.split(":").length === 3) {
            return timeString.substring(0, 5); // Return first 5 characters (HH:mm)
          }
          return timeString;
        };

        // Format logo/alternate_logo URLs to file uploader format (array with uploadUrl/path)
        const formatLogoForUploader = (logoUrl) => {
          if (!logoUrl || logoUrl === "") return [];

          // If API returns a relative path like "uploads/xxx.png",
          // make sure it at least has a leading slash so <img src> works correctly
          let normalizedUrl = logoUrl;
          if (
            typeof normalizedUrl === "string" &&
            !normalizedUrl.startsWith("http://") &&
            !normalizedUrl.startsWith("https://")
          ) {
            if (!normalizedUrl.startsWith("/")) {
              normalizedUrl = `/${normalizedUrl}`;
            }
          }

          return [
            {
              uploadUrl: normalizedUrl,
              path: normalizedUrl,
              name: normalizedUrl.split("/").pop() || "logo",
              loading: false,
              error: false,
              failed: false,
            },
          ];
        };

        const InitialData = {
          id: ws.id, // keep ID for update

          // Basic Information
          // Backend response fields: logo (main), logo2 (alternate)
          logo: formatLogoForUploader(ws.logo),
          alternate_logo: formatLogoForUploader(ws.logo2),
          workspace_name: ws.name || "",
          tagline: ws.tagline || "",
          ws_start: formatTimeForInput(ws.ws_start) || "",
          ws_end: formatTimeForInput(ws.ws_end) || "",
          ws_minutes: ws.ws_minutes || "",
          ws_relax_time: ws.ws_relax_time || "",

          // Contact Information
          country_code2: ws.country_code2 || "",
          phone: ws.phone || "",
          phone2: ws.phone2 || "",
          email: ws.email || "",
          website: ws.website || "",

          // Notification Information
          is_email_notification: ws.is_email_notification === 1,
          is_sms_notification: ws.is_sms_notification === 1,
          is_push_notification: ws.is_push_notification === 1,

          // Social Information
          instagram: ws.instagram || "",
          linkedin: ws.linkedin || "",
          twitter: ws.twitter || "",
          facebook: ws.facebook || "",

          // Location Information
          address: ws.address || "",
          latitude: ws.latitude || "",
          longitude: ws.longitude || "",
        };
        setWorkspaceDetail(InitialData);
        setIsEditValue(InitialData); // so handleSubmit can access id
      })
      .catch((error) => {
        toast.error("Failed to fetch workspace data!");
      });
  };

  useEffect(() => {
    fetchWorkspaceDetail();
  }, []);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // Convert time format from HH:mm to HH:mm:ss
    const formatTimeToHMS = (timeString) => {
      if (!timeString || timeString === "") return "";
      // If already in HH:mm:ss format, return as is
      if (timeString.split(":").length === 3) return timeString;
      // If in HH:mm format, add :00 for seconds
      if (timeString.split(":").length === 2) return `${timeString}:00`;
      return timeString;
    };

    // Convert to integer or 0 if empty
    const toInteger = (value) => {
      if (value === "" || value === null || value === undefined) return 0;
      const num = parseInt(value, 10);
      return isNaN(num) ? 0 : num;
    };

    const getLogoUrl = (logoValue) => {
      // If it's a string (existing logo URL), return it directly
      if (typeof logoValue === "string" && logoValue.trim() !== "") {
        return logoValue;
      }
      
      // If it's an array of file objects, extract the first valid URL
      if (Array.isArray(logoValue) && logoValue.length > 0) {
        const validFile = logoValue.find(
          (file) => file.serverResponse || file.uploadUrl || file.path
        );
        
        if (validFile) {
          // Prefer serverResponse.url (from upload response) - this is the direct URL
          if (validFile.serverResponse?.url) {
            return validFile.serverResponse.url;
          } 
          // Fallback to serverResponse.path if url not available
          else if (validFile.serverResponse?.path) {
            return validFile.serverResponse.path;
          } 
          // Fallback to uploadUrl
          else if (validFile.uploadUrl) {
            return validFile.uploadUrl;
          } 
          // Last fallback to path
          else if (validFile.path) {
            return validFile.path;
          }
        }
      }
      
      return "";
    };

    // Extract alternate_logo file URL from the alternate_logo field
    // Handles both: array of file objects (new uploads) and string (existing logo URL)
    // Returns a single string URL (API expects string, not array)
    const getAlternateLogoUrl = (alternateLogoValue) => {
      // If it's a string (existing logo URL), return it directly
      if (typeof alternateLogoValue === "string" && alternateLogoValue.trim() !== "") {
        return alternateLogoValue;
      }
      
      // If it's an array of file objects, extract the first valid URL
      if (Array.isArray(alternateLogoValue) && alternateLogoValue.length > 0) {
        const validFile = alternateLogoValue.find(
          (file) => file.serverResponse || file.uploadUrl || file.path
        );
        
        if (validFile) {
          // Prefer serverResponse.url (from upload response) - this is the direct URL
          if (validFile.serverResponse?.url) {
            return validFile.serverResponse.url;
          } 
          // Fallback to serverResponse.path if url not available
          else if (validFile.serverResponse?.path) {
            return validFile.serverResponse.path;
          } 
          // Fallback to uploadUrl
          else if (validFile.uploadUrl) {
            return validFile.uploadUrl;
          } 
          // Last fallback to path
          else if (validFile.path) {
            return validFile.path;
          }
        }
      }
      
      return "";
    };

    try {
      const payload = {
        ...values,
        logo: getLogoUrl(values.logo),
        alternate_logo: getAlternateLogoUrl(values.alternate_logo),
        // Format time fields to HH:mm:ss
        ws_start: formatTimeToHMS(values.ws_start),
        ws_end: formatTimeToHMS(values.ws_end),
        // Convert to integers
        ws_minutes: toInteger(values.ws_minutes),
        ws_relax_time: toInteger(values.ws_relax_time),
        // Notification flags
        is_email_notification: values.is_email_notification ? 1 : 0,
        is_sms_notification: values.is_sms_notification ? 1 : 0,
        is_push_notification: values.is_push_notification ? 1 : 0,
        country_code2: values.countryCode,
      };

      if (!workspaceDetail?.id) {
        toast.error("Workspace ID not found!");
        setSubmitting(false);
        return;
      }

      await handleUpdate(payload, workspaceDetail.id, updateWorkspace, {
        successMessage: "Workspace updated successfully!",
        errorMessage: "Failed to update workspace!",
        onSuccess: () => {
          fetchWorkspaceDetail(); // refresh after update
        },
      });

      fetchWorkspaceDetail();
      setSubmitting(false);
      setTimeout(() => {
        resetForm();
        setIsEditValue();
      }, 1000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to update workspace!");
      setSubmitting(false);
    }
  };

  const items = [{ label: "General Setup" }];

  const comprehensiveFields = [
    {
      type: "section_header",
      name: "contact_section",
      label: "Basic Information",
      colSpan: 12,
      // className: "border-b pb-2 mb-2 mt-4 text-xl font-semibold",
    },
    {
      name: "logo",
      uploadLabel: "Upload Main Logo (200x200)",
      type: "file",
      required: true,
      accept: ["png"],
      info: "PNG format with a maximum file size of 10 MB and a required 200x200 aspect ratio.",
      subHeading: "Format: PNG, Dimensions: 200x200 px.",
      colSpan: 4,
      // Show logo as a rounded avatar with hover edit icon
      avatar: true,
      avatarSizeClass: "w-20 h-20",
    },
    // {
    //   name: "alternate_logo",
    //   uploadLabel: "Upload Alternate Logo",
    //   type: "file",
    //   required: false,
    //   accept: ["jpg", "jpeg", "png"],
    //   subHeading: "Supported formats: JPEG, JPG, and PNG.",
    //   colSpan: 4,
    // },

    // General Information
    {
      type: "section_header",
      name: "contact_section",
      label: "General Information",
      colSpan: 12,
      // className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "workspace_name",
      label: "Workspace/Campus Name",
      type: "text",
      required: true,
      readOnly: true,
      colSpan: 3,
    },
    {
      name: "tagline",
      label: "Tagline",
      type: "text",
      colSpan: 3,
    },
    {
      name: "ws_start",
      label: "Workspace Start Time",
      type: "time",
      required: true,
      colSpan: 3,
    },
    {
      name: "ws_end",
      label: "Workspace End Time",
      type: "time",
      required: true,
      colSpan: 3,
    },
    {
      name: "ws_minutes",
      label: "Working Hours (minutes)",
      type: "number",
      required: true,
      colSpan: 3,
    },
    {
      name: "ws_relax_time",
      label: "Attendance Relax Time (minutes)",
      type: "number",
      colSpan: 3,
    },

    // Contact Information
    {
      type: "section_header",
      name: "contact_section",
      label: "Contact Information",
      colSpan: 12,
      // className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "phone",
      label: "Phone Number",
      type: "contact",
      countryCodeName: "country_code",
      required: true,
      colSpan: 3,
    },
    {
      name: "phone2",
      label: "Alternate Phone Number",
      type: "contact",
      countryCodeName: "country_code",
      colSpan: 3,
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      readOnly: true,
      colSpan: 3,
    },

    {
      name: "website",
      label: "Website",
      type: "text",
      colSpan: 3,
    },

    // Notification Modes (checkboxes)
    {
      type: "section_header",
      name: "contact_section",
      label: "Notification Information",
      colSpan: 12,
      // className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "is_email_notification",
      label: "Email Notification",
      type: "checkbox",
      colSpan: 2,
    },
    {
      name: "is_sms_notification",
      label: "SMS Notification",
      type: "checkbox",
      colSpan: 2,
    },
    {
      name: "is_push_notification",
      label: "Push Notification",
      type: "checkbox",
      colSpan: 2,
    },

    // Social Links
    {
      type: "section_header",
      name: "contact_section",
      label: "Social Information",
      colSpan: 12,
      // className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "instagram",
      label: "Instagram Link",
      type: "text",
      colSpan: 3,
    },
    {
      name: "linkedin",
      label: "Linkedin Link",
      type: "text",
      colSpan: 3,
    },
    {
      name: "twitter",
      label: "Twitter Link",
      type: "text",
      colSpan: 3,
    },
    {
      name: "facebook",
      label: "Facebook Link",
      type: "text",
      colSpan: 3,
    },

    // Location Information
    {
      type: "section_header",
      name: "contact_section",
      label: "Location Information",
      colSpan: 12,
      // className: "border-b pb-2 mb-2 mt-6 text-xl font-semibold",
    },
    {
      name: "address",
      label: "Address",
      type: "textarea",
      required: true,
      readOnly: true,
    },
    {
      name: "latitude",
      label: "Latitude",
      type: "text",
      // required: true,
      readOnly: true,
      colSpan: 3,
    },
    {
      name: "longitude",
      label: "Longitude",
      type: "text",
      // required: true,
      readOnly: true,
      colSpan: 3,
    },
  ];

  return (
    <div className="flex flex-col gap-4 relative">
      <PageTableHeader
        breadcrumbs={items}
        isSearch={false}
        onSearchChange={(e) => setSearch(e.target.value)}
        isFilter={false}
        buttons={false}
      />
      <div className="flex flex-col bg-white gap-6 p-4">
        <CommonForm
          fields={comprehensiveFields}
          initialData={workspaceDetail || {}}
          onSubmit={handleSubmit}
          submitButtonText="Submit"
          cancelButtonText="Reset"
          columnsPerRow={1}
          gap="gap-4"
          rowClassName=""
          fieldClassName="bg-white p-3 rounded shadow-sm"
        />
      </div>
    </div>
  );
};

export default GeneralSetup;
